import React from 'react';
import BaseClass from 'components/BaseClass';
import LayoutCard from 'components/FrictionlessCheckout/LayoutCard/LayoutCard';
import { wrapComponent } from 'utils/framework';
import {
    Box, Text, Icon, Flex
} from 'components/ui';
import PaymentsDisplay from 'components/FrictionlessCheckout/Payment/PaymentsDisplay/PaymentsDisplay';
import PaymentMethodList from 'components/FrictionlessCheckout/Payment/PaymentMethodList';
import anaConsts from 'analytics/constants';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import StringUtils from 'utils/String';
import { SECTION_NAMES, FC_CHECKOUT_SECTIONS, FC_CHECKOUT_PATH } from 'constants/frictionlessCheckout';
import OrderUtils from 'utils/Order';
import { PAYMENT_TYPES } from 'constants/RwdBasket';
import Venmo from 'utils/Venmo';
import agentAwareUtils from 'utils/AgentAware';

class Payment extends BaseClass {
    state = {
        isHigherRedeemPoints: this.props.zeroDollarHigherRedeemPointsNotCVV || this.props.zeroDollarHigherRedeemPointsCVV
    };

    isNewUserWithVenmoExpress = false;

    setEditChangeLink = (isEditView, isTriggeredByUserClick = false) => {
        if (isEditView) {
            this.props.getCreditCards(this.props.orderId);
            FrictionlessCheckoutBindings.setChangeLinkAnalytics(anaConsts.PAGE_TYPES.PAYMENT, isTriggeredByUserClick);
            this.props.goToURL({ path: `${FC_CHECKOUT_PATH}/${FC_CHECKOUT_SECTIONS.PAYMENT.path}` });
        }
    };

    componentDidMount() {
        const { orderDetails, tmpCardMessage, gcAmountNotEnoughMessage } = this.props;
        this.props.checkSelectedPayment({ orderDetails });

        if (tmpCardMessage) {
            this.loadTempCardMessageModal(tmpCardMessage);
        }

        if (gcAmountNotEnoughMessage) {
            this.setGiftCardAmountNotEnoughMessage();
        }

        if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
            //If it is Sephora Mirror,for certain roles agent should see payment section open and cvv box opened.

            if (!OrderUtils.isZeroCheckout() && !agentAwareUtils.isAgentAuthorized(['3'])) {
                const updatedOrderDetails = {
                    ...this.props.orderDetails
                };

                // Find and update CreditCardPaymentGroup's isComplete to false
                if (updatedOrderDetails.paymentGroups && Array.isArray(updatedOrderDetails.paymentGroups.paymentGroupsEntries)) {
                    updatedOrderDetails.paymentGroups.paymentGroupsEntries = updatedOrderDetails.paymentGroups.paymentGroupsEntries.map(entry => {
                        if (entry.paymentGroupType === 'CreditCardPaymentGroup' && entry.paymentGroup) {
                            return {
                                ...entry,
                                paymentGroup: {
                                    ...entry.paymentGroup,
                                    isComplete: false
                                }
                            };
                        }

                        return entry;
                    });
                }

                this.props.dispatchOrderUpdate(updatedOrderDetails);
                this.setEditChangeLink(true);
            }
        }
    }

    setGiftCardAmountNotEnoughMessage = () => {
        this.props.setCheckoutSectionErrors({ Payment: this.props.gcAmountNotEnoughMessage });
    };

    loadTempCardMessageModal = tmpCardMessage => {
        this.props.showInfoModal({
            isOpen: true,
            title: StringUtils.capitalize(tmpCardMessage.type),
            message: tmpCardMessage.messages.join('. ')
        });
    };

    closeEditState = () => {
        this.setState({
            isHigherRedeemPoints: false
        });
        this.props.goToURL({ path: FC_CHECKOUT_PATH });
    };

    shouldOpenPaymentSection = () => {
        const {
            alternateMethodSelected,
            isPaypalSelected,
            payPalPaymentGroup,
            giftCardPaymentGroups,
            creditCardPaymentGroup,
            isPaymentComplete,
            isShippableOrder,
            hasSavedAddress,
            forcePaymentSectionRender,
            isEditViewFromURL
        } = this.props;

        const hasNoPaymentMethod =
            !alternateMethodSelected &&
            !isEditViewFromURL &&
            !isPaypalSelected &&
            !payPalPaymentGroup?.isComplete && // If paypayl is complete, no need to open payment section
            !giftCardPaymentGroups.length > 0 &&
            (!creditCardPaymentGroup || !creditCardPaymentGroup.isComplete) &&
            !isPaymentComplete &&
            (!isShippableOrder || hasSavedAddress);

        const shouldOpenForRedeemPoints = !isEditViewFromURL && this.state.isHigherRedeemPoints && !forcePaymentSectionRender;

        if (this.props.skipPaymentAutoOpen) {
            return false;
        }

        return hasNoPaymentMethod || shouldOpenForRedeemPoints;
    };

    // SHP-3342: Close payment section on mWeb when PayPal completes.
    // mWeb uses URL-based routing (isEditViewFromURL) instead of component state.
    // Detect PayPal incomplete → complete and navigate away from /payment to collapse the section.
    shouldClosePaymentSection = prevProps => {
        const { payPalPaymentGroup, isEditViewFromURL } = this.props;

        return (
            isEditViewFromURL &&
            !prevProps.payPalPaymentGroup?.isComplete && // PayPal was incomplete before
            payPalPaymentGroup?.isComplete // PayPal is now complete (user returned from PayPal auth)
        );
    };

    componentDidUpdate(prevProps) {
        const {
            isNewUserFlow,
            tmpCardMessage,
            orderDetails,
            activeSection,
            availablePaymentMethods,
            isAlternatePaymentMethodSelected,
            isEditViewFromURL
        } = this.props;

        if (this.shouldOpenPaymentSection() && activeSection !== SECTION_NAMES.PAYMENT) {
            this.props.setActiveSection(SECTION_NAMES.PAYMENT);
            this.setAnalytics();
        }

        if (
            !isEditViewFromURL &&
            prevProps.isEditViewFromURL &&
            isAlternatePaymentMethodSelected(orderDetails, availablePaymentMethods) &&
            !this.props.alternateMethodSelected
        ) {
            this.props.checkSelectedPayment({ orderDetails: this.props.orderDetails });
        }

        // Make sure we close payment section when PayPal completes on mWeb (SHP-3342)
        // Navigates away from /payment URL, collapsing the section
        if (this.shouldClosePaymentSection(prevProps)) {
            this.closeEditState();
        }

        if (isEditViewFromURL && prevProps.isNewUserFlow && !isNewUserFlow) {
            this.setAnalytics();
        }

        if (prevProps.orderDetails !== orderDetails && tmpCardMessage && activeSection === SECTION_NAMES.PAYMENT) {
            this.loadTempCardMessageModal(tmpCardMessage);
        }

        this.checkSelectedPayment(prevProps);

        this.validateGiftCardAmountNotEnoughMessage(prevProps);
    }

    checkSelectedPayment(prevProps) {
        const { isNewUserFlow, alternatePaymentName } = this.props;

        if (!this.isNewUserWithVenmoExpress) {
            this.isNewUserWithVenmoExpress = !!(isNewUserFlow && alternatePaymentName === PAYMENT_TYPES.PAY_VENMO && Venmo.isVenmoExpressFlow());
        }

        if (prevProps.orderDetails !== this.props.orderDetails && this.isNewUserWithVenmoExpress) {
            this.props.checkSelectedPayment({ orderDetails: this.props.orderDetails });
        }
    }

    setAnalytics = () => {
        const { zeroDollarHigherRedeemPointsNotCVV, paymentOptions, isNewUserFlow } = this.props;
        const openCreditCardForm = zeroDollarHigherRedeemPointsNotCVV && !paymentOptions?.creditCards?.length;
        const pageDetail = openCreditCardForm ? anaConsts.PAGE_NAMES.ADD_CREDIT_CARD : anaConsts.PAGE_TYPES.PAYMENT;

        !isNewUserFlow && FrictionlessCheckoutBindings.setChangeLinkAnalytics(pageDetail);
    };

    validateGiftCardAmountNotEnoughMessage = prevProps => {
        const { gcAmountNotEnoughMessage, alternateMethodSelected, orderDetails, giftCardAmountEqualsOrderTotal } = this.props;

        if (prevProps.orderDetails !== orderDetails && gcAmountNotEnoughMessage) {
            this.setGiftCardAmountNotEnoughMessage();
        }

        const shouldClearSectionErrors = prevProps.gcAmountNotEnoughMessage && alternateMethodSelected;

        if (prevProps.orderDetails !== orderDetails && shouldClearSectionErrors) {
            this.props.clearCheckoutSectionErrors();
        }

        if (
            prevProps.giftCardAmountEqualsOrderTotal !== giftCardAmountEqualsOrderTotal &&
            giftCardAmountEqualsOrderTotal &&
            this.props.alternatePaymentName === PAYMENT_TYPES.PAY_VENMO
        ) {
            this.props.commonOrderToggleActions();
        }
    };

    render() {
        const {
            sectionNumber,
            localization,
            isPaymentComplete,
            creditCardPaymentGroup,
            giftCardPaymentGroups,
            availablePaymentMethods,
            isPaypalSelected,
            payPalPaymentGroup,
            alternateMethodSelected,
            isNewUserFlow,
            storeCredits,
            hasGiftCardInOrder,
            alternatePaymentName,
            installmentValue,
            isCombinableGiftCards,
            showErrorIcon,
            shouldRenderGiftCardSection,
            isGuestCheckout,
            zeroDollarHigherRedeemPointsCVV,
            zeroDollarHigherRedeemPointsNotCVV,
            paymentOptions,
            venmoUsername,
            sectionLevelError,
            isEfullfillmentOrder,
            gcAmountNotEnoughMessage,
            orderHasPhysicalGiftCard,
            renderSubTitle,
            isPhysicalGiftCardOnly,
            isPhysicalGiftCardAndHardGoodShippingGroup,
            isEditViewFromURL,
            hasNoPaymentOptionSelected
        } = this.props;

        const { isAfterpayEnabledForThisOrder, isKlarnaEnabledForThisOrder, isVenmoEnabledForThisOrder, isPazeEnabledForThisOrder } =
            availablePaymentMethods;

        return (
            <LayoutCard
                sectionInfo={{
                    sectionNumber: (showErrorIcon && !isNewUserFlow && !isGuestCheckout) || sectionLevelError ? null : sectionNumber,
                    title: localization.cardTitle,
                    hasDivider: true,
                    isChangePermitted: !isEditViewFromURL,
                    onChangeClick: this.setEditChangeLink,
                    renderErrorView: (showErrorIcon && !isNewUserFlow && !isGuestCheckout) || !!sectionLevelError,
                    removeMarginTop: isEditViewFromURL,
                    sectionLevelError,
                    sectionName: SECTION_NAMES.PAYMENT,
                    ...(renderSubTitle && {
                        subTitle: localization.somePaymentCannotUsed
                    })
                }}
                marginTop={[4, 4, 5]}
                hasPaddingForChildren={false}
                isEditMode={isEditViewFromURL}
                isCollapsed={isNewUserFlow || (!isEditViewFromURL && hasNoPaymentOptionSelected) || this.props.skipPaymentAutoOpen}
                ariaLabel={localization.paymentInformation}
                role='region'
                sectionIcon={
                    <Flex flexDirection='row'>
                        <Icon
                            name='lock'
                            size='1.25em'
                            marginRight='.4em'
                            aria-hidden='true'
                        />
                        <Text color='gray'>{localization.secure}</Text>
                    </Flex>
                }
            >
                {!isEditViewFromURL ? (
                    <PaymentsDisplay
                        isPaymentComplete={isPaymentComplete}
                        creditCardPaymentGroup={creditCardPaymentGroup}
                        isPaypalSelected={isPaypalSelected}
                        payPalPaymentGroup={payPalPaymentGroup}
                        venmoUsername={venmoUsername}
                        storeCredits={storeCredits}
                        giftCardPaymentGroups={giftCardPaymentGroups}
                        hasGiftCardInOrder={hasGiftCardInOrder || !isCombinableGiftCards}
                        localization={localization}
                        alternatePaymentName={alternatePaymentName}
                        alternateMethodSelected={alternateMethodSelected}
                        checkoutEnabled={
                            isAfterpayEnabledForThisOrder || isKlarnaEnabledForThisOrder || isVenmoEnabledForThisOrder || isPazeEnabledForThisOrder
                        }
                        installmentValue={installmentValue}
                        showError={showErrorIcon}
                        isEfullfillmentOrder={isEfullfillmentOrder}
                        shouldRenderGiftCardSection={isPhysicalGiftCardAndHardGoodShippingGroup}
                    />
                ) : (
                    <Box paddingX={[4, 4, 5]}>
                        <PaymentMethodList
                            {...availablePaymentMethods}
                            setEditChangeLink={this.setEditChangeLink}
                            alternateMethodSelected={alternateMethodSelected}
                            alternatePaymentName={alternatePaymentName}
                            showGiftCard={giftCardPaymentGroups.length < 2 && shouldRenderGiftCardSection}
                            showEditButtonFirst={showErrorIcon}
                            saveBtnSuccessCallback={this.closeEditState}
                            isGuestCheckout={isGuestCheckout}
                            zeroDollarHigherRedeemPointsCVV={zeroDollarHigherRedeemPointsCVV}
                            openCreditCardForm={zeroDollarHigherRedeemPointsNotCVV && !paymentOptions?.creditCards?.length}
                            storeCredits={storeCredits}
                            isPaymentComplete={isPaymentComplete}
                            orderHasPhysicalGiftCard={orderHasPhysicalGiftCard}
                            showGCAmountNotEnoughMessage={!!gcAmountNotEnoughMessage}
                            isPhysicalGiftCardOnly={isPhysicalGiftCardOnly}
                        />
                    </Box>
                )}
            </LayoutCard>
        );
    }
}

export default wrapComponent(Payment, 'Payment', true);
